'use client';
import { Header as HeaderSparky, LayoutContainer } from '@petsmart-ui/sparky';
import type { Entry } from 'contentful';

import { LegacyIcon, LegacyIconName, useDecisions } from '@libs/common-next';
import { NAVIGATION_URLS } from '@libs/core';
import { Autocomplete } from '../Autocomplete';

import { LegacyAccountDropdown } from '../LegacyAccountDropdown';
import { LegacyHeaderDrawer } from '../LegacyHeaderDrawer';
import { LegacyMinicart } from '../LegacyMinicart';
import { LegacyNavAdoptions } from '../LegacyNavAdoptions';
import { LegacyUsableNet } from '../LegacyUsableNet';

import { buildPage, ComponentResolver } from '../../utils';
import type { LegacyHeaderProps } from './types';
import { LogoWrapper } from '../LogoWrapper';
import LegacyHeaderPersonIcon from './LegacyHeaderPersonIcon';
import { OffersContainerOpti } from '@libs/shared-ui';
import './LegacyHeader.scss';

export default function LegacyHeader({ entry }: Readonly<LegacyHeaderProps>) {
    const { mainMenu, items, livesSaved, secondaryNavigation } = entry?.fields ?? {};
    const children = items ? buildPage(items as Entry[]) : null;
    const abTestFlag = useDecisions('exp_loyalty_off');

    return (
        <>
            {children}
            <HeaderSparky className="legacy-header" data-testid="legacy-header">
                <HeaderSparky.Top className="legacy-header__section legacy-header__section--divide">
                    <LayoutContainer className="legacy-header__container">
                        <LegacyNavAdoptions
                            secondaryNavigation={secondaryNavigation as Entry}
                            livesSaved={livesSaved as Entry}
                        />
                        <LegacyUsableNet />
                    </LayoutContainer>
                </HeaderSparky.Top>
                <HeaderSparky.Middle
                    className={`legacy-header__section legacy-header__section${abTestFlag ? ' legacy-header__opti' : ''}`}
                    hideMenuButton
                >
                    <LayoutContainer className="legacy-header__container" role="banner">
                        <div className="legacy-header__banner--desktop">
                            <LegacyHeaderDrawer
                                secondaryNavigation={secondaryNavigation as Entry}
                                mainMenu={mainMenu as Entry}
                            />
                            <div className="legacy-header__logo">
                                <LogoWrapper />
                            </div>
                            <div className="legacy-header__search">
                                <Autocomplete className="legacy-autocomplete" productsHitPerPage={2} />
                            </div>
                            <div className="legacy-header__account">
                                <LegacyAccountDropdown />
                                <LegacyHeaderPersonIcon />
                            </div>
                            <div className="legacy-header__cart">
                                <a
                                    href={NAVIGATION_URLS.legacy.storeLocator.base}
                                    title="Locate Stores"
                                    className="legacy-header__cart-pin-icon"
                                >
                                    <LegacyIcon aria-hidden="true" name={LegacyIconName.PIN} isPriority size={24} />
                                </a>
                                {/* Optimizely START */}
                                <div className="legacy-header__offers" data-testid="exp_loyalty_off">
                                    <OffersContainerOpti />
                                </div>
                                {/* Optimizely END */}
                                <LegacyMinicart />
                            </div>
                        </div>
                    </LayoutContainer>
                </HeaderSparky.Middle>
                <HeaderSparky.Bottom
                    className="legacy-header__section legacy-header__section--primary"
                    data-testid="legacy-mainmenu"
                >
                    <LayoutContainer className="legacy-header__container">
                        <ComponentResolver entry={mainMenu as Entry} />
                    </LayoutContainer>
                </HeaderSparky.Bottom>
            </HeaderSparky>
        </>
    );
}
